import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { formatIDR, formatIDRShort, formatDate, MONTHS_ID } from "../lib/format";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { BookOpen, Users, Wallet, TrendingUp, Percent, ArrowUpRight, TrendingDown, Minus, ArrowDown, ArrowUp, LogIn, LogOut, AlertCircle, Calendar as CalendarIcon, Globe } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip, LineChart, Line } from "recharts";
import { flagFor } from "../lib/countries";

const nowYear = new Date().getFullYear();
const nowMonth = new Date().getMonth() + 1;
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function Dashboard() {
  const [month, setMonth] = useState(nowMonth);
  const [year, setYear] = useState(nowYear);
  const [stats, setStats] = useState(null);
  const [allBookings, setAllBookings] = useState([]);
  const [profitTrend, setProfitTrend] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, b, p] = await Promise.all([
        api.get(`/dashboard/stats?year=${year}&month=${month}`),
        api.get(`/bookings`),
        api.get(`/dashboard/profit-trend?months=3&end_year=${year}&end_month=${month}`),
      ]);
      setStats(s.data);
      setAllBookings(b.data || []);
      setProfitTrend(p.data.months || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [month, year]);

  // Operational: today's actions + upcoming check-ins (next 7 days)
  const ops = useMemo(() => {
    const today = todayISO();
    const todayDate = new Date(today);
    const in7 = new Date(todayDate);
    in7.setDate(todayDate.getDate() + 7);
    const in7ISO = in7.toISOString().slice(0, 10);

    const isBlocked = (b) => {
      const gn = (b?.guest_name || "").trim().toLowerCase();
      if (b?.notes && /imported from|ical/i.test(b.notes)) return true;
      return ["blocked", "closed", "reserved", "not available", "unavailable"].some(
        (k) => gn === k || gn.startsWith(k)
      );
    };

    const real = allBookings.filter((b) => b.status !== "Cancelled" && !isBlocked(b));
    const checkInToday = real.filter((b) => b.check_in === today);
    const checkOutToday = real.filter((b) => b.check_out === today);
    const unpaid = real.filter((b) => {
      const remaining = (b.total_price || 0) - (b.deposit || 0);
      return b.check_in >= today && remaining > 0 && b.status !== "Paid";
    });
    const upcoming = real
      .filter((b) => b.check_in > today && b.check_in <= in7ISO)
      .sort((a, b) => (a.check_in || "").localeCompare(b.check_in || ""))
      .slice(0, 6);

    return { checkInToday, checkOutToday, unpaid, upcoming };
  }, [allBookings]);

  const platformData = stats
    ? Object.entries(stats.platform_counts || {}).map(([k, v]) => ({ platform: k, bookings: v }))
    : [];

  const years = [nowYear - 1, nowYear, nowYear + 1];

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle="Ringkasan performa homestay bulan ini — booking, tamu, pendapatan, dan occupancy rate."
        actions={
          <>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger data-testid="dashboard-month-select" className="w-[160px] bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS_ID.map((m, i) => (
                  <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger data-testid="dashboard-year-select" className="w-[110px] bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        }
      />

      {/* Today's Action Panel — operational ergonomics */}
      <TodayActionPanel ops={ops} loading={loading} />

      {/* KPI Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
        <KpiCard
          icon={<BookOpen className="h-5 w-5" strokeWidth={1.6} />}
          label="Total Booking"
          value={loading ? "…" : stats?.total_bookings ?? 0}
          accent="#D16B4D"
          testid="kpi-card-total-bookings"
        />
        <KpiCard
          icon={<Users className="h-5 w-5" strokeWidth={1.6} />}
          label="Total Tamu"
          value={loading ? "…" : stats?.total_guests ?? 0}
          accent="#2C7A51"
          testid="kpi-card-total-guests"
        />
        <KpiCard
          icon={<Wallet className="h-5 w-5" strokeWidth={1.6} />}
          label="Pendapatan"
          value={loading ? "…" : formatIDRShort(stats?.revenue || 0)}
          subtext={stats ? formatIDR(stats.revenue) : ""}
          accent="#D16B4D"
          testid="kpi-card-revenue"
        />
        <KpiCard
          icon={<TrendingUp className="h-5 w-5" strokeWidth={1.6} />}
          label="Top Platform"
          value={loading ? "…" : (stats?.top_platform || "-")}
          accent="#082618"
          testid="kpi-card-top-platform"
          valueClass="text-2xl font-heading"
        />
        <KpiCard
          icon={<Percent className="h-5 w-5" strokeWidth={1.6} />}
          label="Occupancy Rate"
          value={loading ? "…" : `${stats?.occupancy_rate ?? 0}%`}
          subtext={stats ? `${stats.occupied_nights}/${stats.total_room_nights} malam` : ""}
          accent="#2C7A51"
          testid="kpi-card-occupancy"
        />
      </div>

      {/* Profit Trend (3 bulan terakhir) */}
      <ProfitTrendCard months={profitTrend} loading={loading} />

      {/* Chart + Recent */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-white border border-[#EBE6DE] rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#586D61]">Channel Mix</div>
              <h3 className="font-heading text-xl font-medium text-[#172E21] mt-1">Booking per Platform</h3>
            </div>
          </div>
          <div className="h-72">
            {platformData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={platformData} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EBE6DE" vertical={false} />
                  <XAxis dataKey="platform" tick={{ fontSize: 11, fill: "#586D61" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#586D61" }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "#FDFBF7" }} contentStyle={{ borderRadius: 12, border: "1px solid #EBE6DE", fontFamily: "DM Sans" }} />
                  <Bar dataKey="bookings" fill="#D16B4D" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-[#586D61]">
                Belum ada data booking untuk bulan ini
              </div>
            )}
          </div>

          {/* Country Mix - secondary section inside same card */}
          <CountryMixSection countryCounts={stats?.country_counts || {}} />
        </div>

        <div className="lg:col-span-2 bg-white border border-[#EBE6DE] rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#586D61]">7 Hari ke Depan</div>
              <h3 className="font-heading text-xl font-medium text-[#172E21] mt-1">Tamu Akan Datang</h3>
            </div>
            <a href="/bookings" className="text-[#D16B4D] text-sm font-medium flex items-center gap-1 hover:underline" data-testid="link-view-all-bookings">
              Lihat Semua <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </div>
          <div className="flex flex-col gap-3">
            {ops.upcoming.length === 0 && (
              <div className="text-sm text-[#586D61] py-8 text-center">
                Tidak ada tamu akan datang dalam 7 hari ke depan.
              </div>
            )}
            {ops.upcoming.map((b) => (
              <div key={b.id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#FDFBF7] transition-colors">
                <div className="h-10 w-10 rounded-full bg-[#2C7A51]/10 flex items-center justify-center text-[#2C7A51] font-heading font-semibold flex-shrink-0">
                  {b.guest_name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#172E21] truncate">{b.guest_name}</div>
                  <div className="text-xs text-[#586D61] truncate">
                    {b.room_name} · {formatDate(b.check_in)} ({b.nights} mlm)
                  </div>
                </div>
                <StatusBadge status={b.status} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function CountryMixSection({ countryCounts }) {
  const entries = Object.entries(countryCounts || {})
    .filter(([k]) => k && k !== "Tidak Disebut")
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);
  const total = entries.reduce((s, [, n]) => s + n, 0);
  if (total === 0) return null;
  return (
    <div className="mt-6 pt-5 border-t border-[#EBE6DE]" data-testid="country-mix-section">
      <div className="flex items-center gap-2 mb-3">
        <Globe className="h-4 w-4 text-[#2C7A51]" />
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#586D61]">Asal Tamu</div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {entries.map(([country, count]) => {
          const pct = total > 0 ? ((count / total) * 100).toFixed(0) : 0;
          return (
            <div
              key={country}
              data-testid={`country-stat-${country}`}
              className="flex items-center gap-2.5 p-2.5 rounded-xl border border-[#EBE6DE] bg-[#FDFBF7] hover:bg-white transition-colors"
            >
              <span className="text-2xl leading-none flex-shrink-0">{flagFor(country)}</span>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-[#172E21] truncate" title={country}>{country}</div>
                <div className="text-[10px] text-[#586D61] font-mono">{count} tamu · {pct}%</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TodayActionPanel({ ops, loading }) {
  const todayLabel = new Date().toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const totalActions = ops.checkInToday.length + ops.checkOutToday.length + ops.unpaid.length;
  if (loading) {
    return (
      <div className="bg-[#FDFBF7] border border-[#EBE6DE] rounded-2xl p-5 animate-pulse">
        <div className="h-4 w-40 bg-[#EBE6DE] rounded mb-3" />
        <div className="grid grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-20 bg-white rounded-xl" />)}
        </div>
      </div>
    );
  }
  if (totalActions === 0) {
    return (
      <div data-testid="today-action-empty" className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center">
            <CalendarIcon className="h-5 w-5" />
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-800">Hari Ini · {todayLabel}</div>
            <div className="font-heading text-base font-medium text-emerald-900 mt-0.5">Semua aman — tidak ada agenda check-in / check-out / pembayaran tertunda hari ini.</div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="today-action-panel" className="bg-white border border-[#D16B4D]/30 rounded-2xl shadow-sm overflow-hidden">
      <div className="bg-[#D16B4D] text-white px-5 py-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <CalendarIcon className="h-4 w-4" />
          <div className="text-[11px] font-bold uppercase tracking-[0.22em]">Agenda Hari Ini · {todayLabel}</div>
        </div>
        <div className="text-xs font-mono opacity-90">{totalActions} task</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[#EBE6DE]">
        <ActionCell
          icon={LogIn}
          color="#2C7A51"
          label="Check-in Hari Ini"
          items={ops.checkInToday}
          emptyText="Tidak ada tamu datang"
          renderItem={(b) => `${b.guest_name} → ${b.room_name}`}
          testid="action-checkin"
        />
        <ActionCell
          icon={LogOut}
          color="#586D61"
          label="Check-out Hari Ini"
          items={ops.checkOutToday}
          emptyText="Tidak ada tamu pulang"
          renderItem={(b) => `${b.guest_name} ← ${b.room_name}`}
          testid="action-checkout"
        />
        <ActionCell
          icon={AlertCircle}
          color="#D16B4D"
          label="Pembayaran Pending"
          items={ops.unpaid}
          emptyText="Semua lunas"
          renderItem={(b) => {
            const remaining = (b.total_price || 0) - (b.deposit || 0);
            return `${b.guest_name} · sisa ${formatIDRShort(remaining)}`;
          }}
          testid="action-unpaid"
        />
      </div>
    </div>
  );
}

function ActionCell({ icon: Icon, color, label, items, emptyText, renderItem, testid }) {
  return (
    <div data-testid={testid} className="p-4 min-h-[120px]">
      <div className="flex items-center gap-2 mb-3">
        <div className="h-7 w-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}18`, color }}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#586D61]">{label}</div>
        <div className="ml-auto font-mono text-sm font-bold" style={{ color }}>{items.length}</div>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-[#586D61]/70 italic">{emptyText}</div>
      ) : (
        <ul className="space-y-1">
          {items.slice(0, 4).map((b) => (
            <li key={b.id} className="text-xs text-[#172E21] truncate" title={renderItem(b)}>
              <span className="inline-block h-1.5 w-1.5 rounded-full mr-2 align-middle" style={{ backgroundColor: color }} />
              {renderItem(b)}
            </li>
          ))}
          {items.length > 4 && (
            <li className="text-xs text-[#586D61] italic">+{items.length - 4} lainnya</li>
          )}
        </ul>
      )}
    </div>
  );
}

function KpiCard({ icon, label, value, subtext, accent, testid, valueClass }) {
  return (
    <div data-testid={testid} className="kpi-card bg-white border border-[#EBE6DE] rounded-2xl p-6 shadow-sm flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${accent}18`, color: accent }}>
          {icon}
        </div>
      </div>
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#586D61]">{label}</div>
        <div className={`mt-2 ${valueClass || "text-3xl font-mono font-medium tracking-tight text-[#172E21]"}`}>{value}</div>
        {subtext && <div className="text-xs text-[#586D61] mt-1 font-mono">{subtext}</div>}
      </div>
    </div>
  );
}

function ProfitTrendCard({ months, loading }) {
  const last = months[months.length - 1];
  const prev = months[months.length - 2];
  const oldest = months[0];

  const overallTrend = (() => {
    if (!last || !oldest) return null;
    if (oldest.profit === 0 && last.profit === 0) return null;
    if (oldest.profit === 0) return { pct: 100, dir: "up" };
    const delta = ((last.profit - oldest.profit) / Math.abs(oldest.profit)) * 100;
    return { pct: delta, dir: delta > 0 ? "up" : delta < 0 ? "down" : "flat" };
  })();

  return (
    <div
      data-testid="profit-trend-card"
      className="bg-white border border-[#EBE6DE] rounded-2xl p-6 shadow-sm"
    >
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#586D61]">Profit & Loss</div>
          <h3 className="font-heading text-xl font-medium text-[#172E21] mt-1">Tren Profit 3 Bulan Terakhir</h3>
        </div>
        {overallTrend && (
          <div
            data-testid="overall-trend-badge"
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
              overallTrend.dir === "up"
                ? "bg-emerald-100 text-emerald-800"
                : overallTrend.dir === "down"
                ? "bg-rose-100 text-rose-800"
                : "bg-stone-200 text-stone-800"
            }`}
          >
            {overallTrend.dir === "up" ? <ArrowUp className="h-4 w-4" /> : overallTrend.dir === "down" ? <ArrowDown className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
            <span className="font-mono">
              {overallTrend.dir === "flat" ? "—" : `${overallTrend.pct >= 0 ? "+" : ""}${overallTrend.pct.toFixed(0)}%`}
            </span>
            <span className="text-xs opacity-70">vs awal periode</span>
          </div>
        )}
      </div>

      {/* 3 mini cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        {loading || months.length === 0
          ? [0, 1, 2].map((i) => (
              <div key={i} className="rounded-xl border border-[#EBE6DE] p-4 bg-[#FDFBF7] animate-pulse">
                <div className="h-3 w-16 bg-[#EBE6DE] rounded mb-3" />
                <div className="h-7 w-32 bg-[#EBE6DE] rounded mb-2" />
                <div className="h-3 w-20 bg-[#EBE6DE] rounded" />
              </div>
            ))
          : months.map((m, idx) => {
              const previous = months[idx - 1];
              let delta = null;
              if (previous && previous.profit !== 0) {
                delta = ((m.profit - previous.profit) / Math.abs(previous.profit)) * 100;
              } else if (previous && previous.profit === 0 && m.profit !== 0) {
                delta = m.profit > 0 ? 100 : -100;
              }
              const isCurrent = idx === months.length - 1;
              const positive = m.profit >= 0;
              return (
                <div
                  key={`${m.year}-${m.month}`}
                  data-testid={`trend-month-${m.year}-${m.month}`}
                  className={`rounded-xl border p-4 transition-all ${
                    isCurrent
                      ? "border-[#D16B4D]/40 bg-[#D16B4D]/5 shadow-md"
                      : "border-[#EBE6DE] bg-white"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#586D61]">
                      {m.label}
                      {isCurrent && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-[#D16B4D] text-white text-[8px] tracking-wider">SEKARANG</span>}
                    </div>
                    {delta !== null && (
                      <div
                        className={`flex items-center gap-0.5 text-[11px] font-mono font-bold ${
                          delta > 0 ? "text-emerald-700" : delta < 0 ? "text-rose-700" : "text-stone-500"
                        }`}
                      >
                        {delta > 0 ? <ArrowUp className="h-3 w-3" /> : delta < 0 ? <ArrowDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
                        {Math.abs(delta).toFixed(0)}%
                      </div>
                    )}
                  </div>
                  <div className={`font-mono text-2xl font-medium tracking-tight ${positive ? "text-[#2C7A51]" : "text-[#D14D4D]"}`}>
                    {formatIDRShort(m.profit)}
                  </div>
                  <div className="text-[10px] text-[#586D61] font-mono mt-1 flex items-center gap-2">
                    <span className="text-[#D16B4D]">↑ {formatIDRShort(m.revenue)}</span>
                    <span className="text-[#D14D4D]">↓ {formatIDRShort(m.expense)}</span>
                  </div>
                </div>
              );
            })}
      </div>

      {/* Sparkline chart */}
      {months.length > 0 && (
        <div className="h-32 -mx-2">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={months} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EBE6DE" vertical={false} />
              <XAxis dataKey="month_short" tick={{ fontSize: 10, fill: "#586D61" }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip
                formatter={(v) => formatIDR(v)}
                contentStyle={{ borderRadius: 12, border: "1px solid #EBE6DE", fontSize: 12 }}
              />
              <Line
                type="monotone"
                dataKey="profit"
                stroke="#2C7A51"
                strokeWidth={2.5}
                dot={{ r: 5, fill: "#2C7A51", strokeWidth: 2, stroke: "#fff" }}
                activeDot={{ r: 7 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
